/**
 * TextField — text input wired to React Hook Form.
 *
 * Uses @radix-ui/react-label for accessibility.
 * Surfaces validation errors inline beneath the input.
 *
 * See: CLAUDE.md Rule 8
 * See: .plan/features/2026-05-11-framework-adoption.md §3
 */

import React, { useId } from "react";
import { useFormContext, type RegisterOptions, type FieldValues, type Path } from "react-hook-form";
import * as LabelPrimitive from "@radix-ui/react-label";

export interface TextFieldProps<TFieldValues extends FieldValues = FieldValues> {
  /** Must match a key in the form schema. */
  name: Path<TFieldValues>;
  /** Visible label text. Always required for accessibility. */
  label: string;
  /** Optionally hide the label visually (still present for screen readers). */
  labelHidden?: boolean;
  type?: "text" | "email" | "password" | "url" | "tel" | "search";
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Additional RHF register options (validation rules). */
  registerOptions?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  className?: string;
  /** Forwarded to the underlying <input> for Playwright / testing selectors. */
  "data-testid"?: string;
  /**
   * Additional element ID(s) to include in the input's aria-describedby.
   * The component automatically appends the inline error ID when an error
   * is present; this prop allows callers to also associate external hint
   * text (e.g. a helper paragraph outside the field wrapper).
   */
  "aria-describedby"?: string;
}

export function TextField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  labelHidden = false,
  type = "text",
  placeholder,
  autoComplete,
  autoFocus,
  disabled,
  registerOptions,
  className,
  "data-testid": dataTestId,
  "aria-describedby": externalDescribedBy,
}: TextFieldProps<TFieldValues>): React.JSX.Element {
  const uid = useId();
  const inputId = `${uid}-${name}`;
  const errorId = `${uid}-${name}-error`;

  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>();

  // Navigate nested paths (e.g. "address.city")
  const error = name.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, errors) as { message?: string } | undefined;

  const errorMessage = error?.message;

  // Merge external hint ID(s) with the inline error ID so screen readers
  // announce both the hint and the validation message when an error is present.
  const describedBy =
    [externalDescribedBy, errorMessage ? errorId : undefined].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className={className} data-field={name}>
      <LabelPrimitive.Root
        htmlFor={inputId}
        className={labelHidden ? "sr-only" : undefined}
        data-label
      >
        {label}
      </LabelPrimitive.Root>
      <input
        id={inputId}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-invalid={errorMessage ? "true" : undefined}
        aria-describedby={describedBy}
        data-testid={dataTestId}
        {...register(name, registerOptions)}
      />
      {errorMessage && (
        <span id={errorId} role="alert" data-field-error>
          {errorMessage}
        </span>
      )}
    </div>
  );
}
